import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import {
  BookstoreAvailability,
  LibraryAvailability,
} from '®shared/resource-availability';
import { TarlV3Jsonapi } from '®shared/tarl-v3-jsonapi';

import { BibliographicResource } from './bibliographic-resource';
import { CopyBibliographicResourceAction } from './copy-bibliographic-resource-action';
import { UpdateResourceAction } from './update-resource-action';

interface AddPartOfRelationship {
  data: {
    id: string;
    type: string;
  };
  links?: {
    self: string;
    related: string;
  };
  meta?: Record<string, unknown>;
}

@Injectable()
export class BibliographicResourceService {
  constructor(private tarlV3Jsonapi: TarlV3Jsonapi) {}

  createResource(
    resource: BibliographicResource,
  ): Observable<BibliographicResource> {
    if (resource.part_of) {
      return this.createRelatedResources(resource);
    } else {
      return this.createSingleResource(resource);
    }
  }

  copyResource(
    copyResourceAction: CopyBibliographicResourceAction,
  ): Observable<CopyBibliographicResourceAction> {
    const path = this.tarlV3Jsonapi.path('/copy_resource_actions', {});
    return this.tarlV3Jsonapi.postResource<CopyBibliographicResourceAction>(
      path,
      copyResourceAction,
    );
  }

  /**
   * Updates a multi-part BibliographicResource via a POST (NOT partial PATCH)
   */
  updateResourceAction(
    updateResourceAction: UpdateResourceAction,
  ): Observable<UpdateResourceAction> {
    const path = this.tarlV3Jsonapi.path('/update_resource_actions', {});
    return this.tarlV3Jsonapi.postResource<UpdateResourceAction>(
      path,
      updateResourceAction,
    );
  }

  private createSingleResourceWithoutCreatingBookmark(
    resource: BibliographicResource,
  ): Observable<BibliographicResource> {
    const path = this.tarlV3Jsonapi.path('/resources', {});
    const headers = this.tarlV3Jsonapi.getCommonHeaders();
    return this.tarlV3Jsonapi.postResource(path, resource, {
      headers: headers.headers.append('X-Skip-Bookmark-Creation', 'true'),
    });
  }

  createSingleResource(
    resource: BibliographicResource,
  ): Observable<BibliographicResource> {
    const path = this.tarlV3Jsonapi.path('/resources', {});
    return this.tarlV3Jsonapi.postResource(path, resource);
  }

  private createRelatedResources(
    resource: BibliographicResource,
  ): Observable<BibliographicResource> {
    return this.createSingleResourceWithoutCreatingBookmark(
      resource.part_of,
    ).pipe(
      mergeMap(parent =>
        this.createSingleResource(resource).pipe(
          map(child => ({ parent, child })),
        ),
      ),
      mergeMap(({ parent, child }) =>
        this.updateResourcePartOfRelationship(parent, child).pipe(
          map(() => {
            // Reconnect the two resources for what gets returned to the client.
            child.part_of = parent;

            return child;
          }),
        ),
      ),
    );
  }

  updateResourcePartOfRelationship(
    parent: BibliographicResource,
    child: BibliographicResource,
  ): Observable<AddPartOfRelationship> {
    const path = this.tarlV3Jsonapi.path(
      '/resources/{resourceId}/relationships/part_of',
      {
        resourceId: child.id,
      },
    );

    const addPartOfRelationship: AddPartOfRelationship = {
      data: {
        id: parent.id,
        type: 'resources',
      },
    };

    return this.tarlV3Jsonapi.patchRawJsonapiResponse(
      path,
      addPartOfRelationship,
    );
  }

  getLibraryAvailabilityForResource(
    resourceId: string,
  ): Observable<LibraryAvailability> {
    const path = this.tarlV3Jsonapi.path(
      '/resources/{resourceId}/library_holdings',
      {
        resourceId,
      },
    );
    return this.tarlV3Jsonapi.getResource<LibraryAvailability>(path);
  }

  getBookstoreAvailabilityForResource(
    resourceId: string,
    bookstoreId: string,
  ): Observable<BookstoreAvailability> {
    const path = this.tarlV3Jsonapi.path(
      '/resources/{resourceId}/bookstores/{bookstoreId}',
      { resourceId, bookstoreId },
    );
    return this.tarlV3Jsonapi.getResource<BookstoreAvailability>(path);
  }

  getBibliographicResource(
    resourceId: string,
  ): Observable<BibliographicResource> {
    const path = this.tarlV3Jsonapi.path('/resources/{resourceId}', {
      resourceId,
    });
    return this.tarlV3Jsonapi.getResource<BibliographicResource>(path, {
      include: ['part_of'],
    });
  }
}
